import { useDict, useFischer } from 'composables/fischerPlugin';
import type { LongDateFormatKey } from 'moment';
import { computed, ref } from 'vue';

import type { FischerPlugin } from '@/lib/fischerPlugin';

const importMoment = () => import('moment/min/moment-with-locales');

export type MomentLoadedPromise = Promise<Awaited<ReturnType<typeof setupDate>> | null>;

export const setupDate = async (language: string, $dict: any) => {
  const today = new Date();

  const locale = language.slice(0, 2);
  const { default: moment } = await importMoment();
  moment.locale(locale);

  const dateFormatDic = $dict.get('global-Settings.Date-Format');
  const dateFormat = dateFormatDic || 'DD.MM.YYYY';

  const isoShortFormat = 'YYYY-MM-DD';

  const dateFormatLongDic = $dict.get('global-Settings.Date-Format-Long');
  const dateFormatLong = dateFormatLongDic || 'dddd, LL';

  const timeFormatDic = $dict.get('global-Settings.Time-Format');
  const timeFormat = timeFormatDic || 'LT';

  const timeFormatLongDic = $dict.get('global-Settings.Time-Format-Long');
  const timeFormatLong = timeFormatLongDic || 'LTS';

  const dateTimeFormatDic = $dict.get('global-Settings.Date-Time-Format');
  const dateTimeFormat = dateTimeFormatDic || `${dateFormat} ${timeFormat}`;

  return {
    today,
    moment,
    dateFormat,
    isoShortFormat,
    dateFormatLong,
    timeFormat,
    timeFormatLong,
    dateTimeFormat,
  };
};

export const getTimeZoneName = (fischer: FischerPlugin, today: Date): string => {
  const short = today.toLocaleDateString(fischer.siteLanguage());
  const full = today.toLocaleDateString(fischer.siteLanguage(), {
    timeZoneName: 'short',
  });
  const shortIndex = full.indexOf(short);
  if (shortIndex >= 0) {
    const trimmed = full.substring(0, shortIndex) + full.substring(shortIndex + short.length);
    return trimmed.replace(/^[\s,.\-:;]+|[\s,.\-:;]+$/g, '');
  } else {
    return full;
  }
};

export const useDate = () => {
  const fischer = useFischer();
  const dict = useDict();
  const momentLoaded = ref<boolean>(false);
  const momentSetupData = ref<null | Awaited<ReturnType<typeof setupDate>>>(null);

  const timeZoneName = computed<string>(() => {
    if (!momentSetupData.value) {
      return '';
    }
    return getTimeZoneName(fischer, momentSetupData.value.today);
  });

  const getDateFormat = (): string => {
    return momentSetupData.value?.dateFormat || '';
  };

  const getFormattedDate = (date: Date | number, format: string): string | Date | null => {
    if (!date || !format) {
      return null;
    }
    if (!momentSetupData.value) {
      return '';
    }
    if (!momentSetupData.value.moment) {
      console.error('moment not correctly initialized');
      return String(date);
    }
    const momentDate = momentSetupData.value.moment(date);
    if (!momentDate) {
      console.error('Could not parse date', date);
      return String(date);
    }
    return momentDate.format(format);
  };

  const formatDate = (date: Date | number): string | Date | null => {
    return getFormattedDate(date, momentSetupData.value?.dateFormat);
  };

  const formatDateLong = (date: Date | number): string | Date | null => {
    return getFormattedDate(date, momentSetupData.value?.dateFormatLong);
  };

  const formatDateIsoShort = (date: Date | number): string | Date | null => {
    if (!date || !momentSetupData.value) {
      return null;
    }
    return momentSetupData.value.moment(date).format(momentSetupData.value.isoShortFormat);
  };

  const formatTime = (date: Date | number): string | Date | null => {
    return getFormattedDate(date, momentSetupData.value?.timeFormat);
  };

  const formatTimeLong = (date: Date | number): string | Date | null => {
    return getFormattedDate(date, momentSetupData.value?.timeFormatLong);
  };

  const formatDateTime = (date: Date | number): string | Date | null => {
    return getFormattedDate(date, momentSetupData.value?.dateTimeFormat);
  };

  const formatByCustomDateFormat = (
    date: Date | number,
    customDateFormat: string,
  ): string | Date | null => {
    return getFormattedDate(date, customDateFormat);
  };

  const parseDate = (date: Date | number): Date | null => {
    if (!date || !momentSetupData.value) {
      return null;
    }
    return momentSetupData.value.moment(date, momentSetupData.value.dateFormat).toDate();
  };

  const parseDateIsoShort = (date: Date | number): Date | null => {
    if (!date || !momentSetupData.value) {
      return null;
    }
    return momentSetupData.value.moment(date, momentSetupData.value.isoShortFormat).toDate();
  };

  const parseByCustomDateFormat = (date: Date | number, customDateFormat: string): Date | null => {
    if (!date || !momentSetupData.value) {
      return null;
    }
    return momentSetupData.value.moment(date, customDateFormat).toDate();
  };

  const getFormatPattern = (lang: string, format: LongDateFormatKey = 'L'): string | null => {
    if (!lang || !momentSetupData.value) {
      return null;
    }
    return momentSetupData.value.moment().locale(lang).localeData().longDateFormat(format);
  };

  const momentLoadedPromise: MomentLoadedPromise = setupDate(fischer.siteLanguage(), dict)
    .then((data) => {
      momentSetupData.value = data;
      momentLoaded.value = true;
      return data;
    })
    .catch((error: any) => {
      console.error('Could not load moment', error);
      return null;
    });

  return {
    formatByCustomDateFormat,
    formatDate,
    formatDateIsoShort,
    formatDateLong,
    formatDateTime,
    formatTime,
    formatTimeLong,
    getDateFormat,
    getFormatPattern,
    momentLoaded,
    momentLoadedPromise,
    parseByCustomDateFormat,
    parseDate,
    parseDateIsoShort,
    timeZoneName,
  };
};
